import {
  AppointmentDiscount,
  PostAppointmentCreditVariables,
} from "../../../apis/appointmentApi";
import {
  PackageTotalsValues,
  CustomTotalsValues,
  CreditsForm,
  CreditExpiry,
} from "./CreditsModal.types";
import { AppointmentInvoicePreviewFragment } from "./CreditsModal-generated.hooks";

export const sessionTypeOptions = [
  {
    label: "Package",
    value: "PACKAGE",
  },
  {
    label: "Custom",
    value: "CREDIT",
  },
];

export function calculatePackageTotals(
  invoicePreview?: AppointmentInvoicePreviewFragment,
): PackageTotalsValues | undefined {
  if (!invoicePreview) {
    return undefined;
  }

  const subtotalValue = invoicePreview.subtotal;

  const discountPercent =
    invoicePreview?.discount?.type === "%"
      ? invoicePreview?.discount?.rate * 100
      : 0;
  const discountValue = invoicePreview?.discount?.amount;

  const taxPercent = (invoicePreview?.tax?.rate || 0) * 100;
  const taxValue = invoicePreview?.tax?.amount;
  const totalValue = invoicePreview?.total;

  const transactionFee = invoicePreview?.fees?.find(
    (fee) => fee.code === "transactionFee",
  );
  const transactionFeeDescription = transactionFee?.description;
  const transactionFeePercent = (transactionFee?.rate || 0) * 100;
  const transactionFeeValue = transactionFee?.fee;

  return {
    subtotalValue,
    taxPercent,
    taxValue,
    discountPercent,
    discountValue,
    totalValue,
    transactionFeeDescription,
    transactionFeePercent,
    transactionFeeValue,
  };
}

export function calculateCustomTotals(
  invoicePreview?: AppointmentInvoicePreviewFragment,
): CustomTotalsValues | undefined {
  if (typeof invoicePreview?.subtotal !== "number") {
    return undefined;
  }

  const subtotalValue = invoicePreview?.subtotal;
  const taxPercent = (invoicePreview?.tax?.rate || 0) * 100;
  const taxValue = invoicePreview?.tax?.amount;

  const totalValue = invoicePreview?.total;

  const transactionFee = invoicePreview?.fees?.find(
    (fee) => fee.code === "transactionFee",
  );
  const transactionFeeDescription = transactionFee?.description;
  const transactionFeePercent = (transactionFee?.rate || 0) * 100;
  const transactionFeeValue = transactionFee?.fee;

  return {
    subtotalValue,
    taxPercent,
    taxValue,
    totalValue,
    transactionFeeDescription,
    transactionFeePercent,
    transactionFeeValue,
  };
}

// TODO: Refactor this and re-enable Month/Years once backend support is ready.
// Month and week are disabled for now, details at: https://pushpress.atlassian.net/browse/APPT-224
export const timeOptions: {
  label: string;
  value: CreditExpiry;
}[] = [
  {
    label: "Day(s)",
    value: "days",
  },
  // {
  //   label: 'Month(s)', // Disabled option
  //   value: 'months',
  // },
  // {
  //   label: 'Years(s)', // Disabled option
  //   value: 'years',
  // },
];

export function convertCreditsFormToPayload(
  userId: string,
  values: CreditsForm,
  appointmentTypeUuid?: string,
): PostAppointmentCreditVariables["payload"] {
  const creditExpiry = calculateCreditExpiry(
    values.creditsExpireValue,
    values.creditsExpireType,
  );
  if (values.sessionType === "PACKAGE") {
    const appointmentDiscount = values.appointmentDiscount?.uuid;
    let paymentId = values.billing?.uuid;
    if (!paymentId && appointmentDiscount) {
      paymentId = "CASH";
    }
    return {
      purchaseType: values.sessionType!,
      userId,
      appointmentPackage: values.appointmentPackage?.uuid,
      appointmentDiscount,
      paymentId,
    };
  } else if (values.sessionType === "CREDIT") {
    return {
      appointmentType: appointmentTypeUuid || values.appointmentType!.uuid,
      purchaseType: values.sessionType!,
      userId,
      creditNumber: values.numberOfCredits,
      creditExpiry,
      price: values.price,
      taxId: values.tax?.uuid,
      paymentId: values.billing?.uuid,
    };
  } else {
    throw new Error("Invalid session type");
  }
}

const creditExpiryMap: { [key in CreditExpiry]: number } = {
  days: 60 * 60 * 24,
  months: 60 * 60 * 24 * 30,
  years: 60 * 60 * 24 * 30 * 12,
};

function calculateCreditExpiry(
  value?: number | number,
  type?: { value: CreditExpiry },
) {
  if (!value || !type) {
    return undefined;
  }
  return creditExpiryMap[type.value] * value;
}
